/**
 * @file Email Phone Input component.
 */

import React, { Fragment, useState, useEffect, useRef, memo } from 'react';
import PropTypes from 'prop-types';
import TelephoneInput from 'react-telephone-input';
import InputMask from 'react-input-mask';
import classNames from 'classnames';

import Label from './Label';
import { getAsset } from '../../util/assets';
import PhoneIcon from '../icons/PhoneIcon';
import EmailIcon from '../icons/EmailIcon';
import DropdownMenu from './DropdownMenu';
import Button from './Button';
import Angle from './Angle';
import Input from './Input';

export const INPUT_TYPE = {
  PHONE: 'PHONE',
  EMAIL: 'EMAIL',
};

const propTypes = {
  id: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  value: PropTypes.string,
  autoFocus: PropTypes.bool,
  className: PropTypes.string,
  label: PropTypes.string,
  labelNote: PropTypes.string,
  labelSrOnly: PropTypes.bool,
  disabled: PropTypes.bool,
  onPhoneCodeChange: PropTypes.func,
  size: PropTypes.oneOf(['sm']),
  allowInputSelection: PropTypes.bool,
  defaultInputType: PropTypes.oneOf(Object.values(INPUT_TYPE)),
  placeholder: PropTypes.string,
  dropdownDisabled: PropTypes.bool,
};

const EmailPhoneInput = ({
  id,
  value,
  autoFocus,
  className,
  label,
  labelNote,
  disabled,
  size,
  onChange,
  labelSrOnly,
  onPhoneCodeChange,
  defaultInputType = INPUT_TYPE.PHONE,
  onInputTypeChange,
  placeholder,
  dropdownDisabled,
  ...props
}) => {
  const [inputType, setInputType] = useState(defaultInputType);
  const [firstRender, setFirstRender] = useState(true);
  const [maskedPhoneCode, setMaskedPhoneCode] = useState('+1');
  const phoneInputRef = useRef(null);

  const updateInput = event => {
    onChange(event.target.value, inputType === INPUT_TYPE.PHONE);
  };

  const updatePhoneCode = (phone, country) => {
    setMaskedPhoneCode(`+${country.dialCode.replace(/9/g, '\\9')}`);
    onChange(phone, country);
  };

  useEffect(() => {
    const guessedCountry = phoneInputRef.current?.state?.selectedCountry;

    if (guessedCountry) {
      updatePhoneCode(value, guessedCountry);
    }
  }, [phoneInputRef.current]);

  useEffect(() => {
    onPhoneCodeChange?.(maskedPhoneCode);
  }, [maskedPhoneCode]);

  useEffect(() => {
    onInputTypeChange?.(inputType);
  }, [inputType]);

  useEffect(() => {
    if (!firstRender) {
      onChange('', inputType);
    } else {
      setFirstRender(false);
    }
  }, [inputType]);

  const classes = classNames({
    'form-group': true,
    [className]: !!className,
  });

  const containerClasses = classNames({
    'email-or-phone-input dropdown-menu-dark': true,
    'no-dropdown': !!dropdownDisabled,
  });

  const telephoneInputClasses = classNames({
    'phone-input': true,
    'phone-input-sm': size === 'sm',
  });

  const inputMaskClasses = classNames({
    'phone-input-mask form-control': true,
    'form-control-sm': size === 'sm',
  });

  return (
    <div className={classes}>
      {label && (
        <Label
          id={id}
          label={label}
          labelNote={labelNote}
          labelSrOnly={labelSrOnly}
        />
      )}

      <div className={containerClasses}>
        {!dropdownDisabled && (
          <DropdownMenu
            toggle={
              <div className="d-flex align-items-center">
                {inputType === INPUT_TYPE.EMAIL ? (
                  <EmailIcon color="#0d84fe" />
                ) : (
                  <PhoneIcon color="#0d84fe" />
                )}
                <Angle direction="down" className="ml-1" />
              </div>
            }
            toggleClassName="input-selection-dropdown"
            closeOnClickWithin
          >
            <Button
              btnStyle="reset"
              className="dropdown-item text-bold d-flex align-items-center"
              onClick={() => setInputType(INPUT_TYPE.PHONE)}
            >
              <PhoneIcon color="#fff" className="mr-1" /> Phone number
            </Button>
            <Button
              btnStyle="reset"
              className="dropdown-item text-bold d-flex align-items-center"
              onClick={() => setInputType(INPUT_TYPE.EMAIL)}
            >
              &nbsp;
              <EmailIcon color="#fff" className="mr-1" />
              &nbsp;Email address
            </Button>
          </DropdownMenu>
        )}
        {inputType === INPUT_TYPE.PHONE && (
          <Fragment>
            <TelephoneInput
              autoFormat={false}
              classNames={telephoneInputClasses}
              defaultCountry="us"
              flagsImagePath={getAsset('/phone-input/flags.png')}
              onChange={updatePhoneCode}
              value={value}
              preferredCountries={['us']}
              // Force the component to properly format the value
              // on initial mount by focusing on it
              inputProps={{
                autoFocus: true,
              }}
              onFocus={updatePhoneCode}
              disabled={disabled}
              wrappedRef={ref => {
                phoneInputRef.current = ref;
              }}
            />
            <InputMask
              id={id}
              className={inputMaskClasses}
              mask={`${maskedPhoneCode}999999999999999`}
              maskChar={null}
              value={value}
              onChange={updateInput}
              autoFocus={autoFocus}
              alwaysShowMask
              disabled={disabled}
              autoComplete="off"
              {...props}
            />
          </Fragment>
        )}
        {inputType === INPUT_TYPE.EMAIL && (
          <Input
            id={id}
            value={value}
            onChange={updateInput}
            autoFocus={autoFocus}
            disabled={disabled}
            placeholder={placeholder}
            {...props}
          />
        )}
      </div>
    </div>
  );
};

EmailPhoneInput.propTypes = propTypes;

export default memo(EmailPhoneInput);
